import React from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import './index.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import MobileMenu from './component/MobileMenu';
import SiteBrand from './component/SiteBrand';
import ThemeToggle from './component/ThemeToggle';
import ReleaseWhatsNewModal from './components/ReleaseWhatsNewModal';
import { useReleaseWhatsNew } from './components/useReleaseWhatsNew';
import {
  BellOutlined,
  HomeOutlined,
  UserAddOutlined,
  FileTextOutlined,
  AuditOutlined,
  SafetyCertificateOutlined,
  MailOutlined,
  SearchOutlined,
  RocketOutlined,
  ApiOutlined,
} from '@ant-design/icons';
import {
  faTelegramPlane,
  faWhatsapp,
} from '@fortawesome/free-brands-svg-icons';
import { useCommunityLinks } from '../../core/config/useCommunityLinks';
import { isMcpEnabled } from '../../core/config/runtimeConfig';

const isPathActive = (pathname: string, path: string): boolean =>
  path === '/'
    ? pathname === path
    : pathname === path || pathname.startsWith(`${path}/`);

const navLinkClass = (active: boolean): string =>
  `jo-nav-link${active ? ' is-active' : ''}`;

const Public: React.FC = () => {
  const location = useLocation();
  const releaseWhatsNew = useReleaseWhatsNew();
  const { whatsappUrl, telegramUrl, contactEmail } = useCommunityLinks();
  const mcpEnabled = isMcpEnabled();

  return (
    <div className="public-page-bg flex min-h-screen w-full flex-col">
      <ReleaseWhatsNewModal
        isOpen={releaseWhatsNew.isOpen}
        slides={releaseWhatsNew.slides}
        onClose={releaseWhatsNew.close}
      />
      <header
        className="public-site-header fixed left-0 top-0 flex w-full items-center py-3"
        style={{ zIndex: 1000 }}
      >
        <div className="public-content-rail flex w-full flex-row items-center justify-between gap-3">
          <h3 className="m-0 min-w-0">
            <SiteBrand
              to="/"
              markSize={40}
              markClassName="shadow-md"
              wordmarkClassName="hidden text-sm font-black uppercase leading-tight tracking-[0.08em] sm:flex md:text-base"
            />
          </h3>

          <div className="public-header-actions flex items-center gap-2">
            <nav
              className="public-primary-navigation hidden items-stretch lg:flex"
              aria-label="Primary"
            >
              <Link
                to="/"
                className={navLinkClass(isPathActive(location.pathname, '/'))}
                aria-current={
                  isPathActive(location.pathname, '/') ? 'page' : undefined
                }
              >
                <HomeOutlined aria-hidden />
                <span>Home</span>
              </Link>
              <Link
                to="/jobs"
                className={navLinkClass(
                  isPathActive(location.pathname, '/jobs')
                )}
                aria-current={
                  isPathActive(location.pathname, '/jobs') ? 'page' : undefined
                }
              >
                <SearchOutlined aria-hidden />
                <span>Find Jobs</span>
              </Link>
              <Link
                to="/subscribe"
                className={navLinkClass(
                  isPathActive(location.pathname, '/subscribe')
                )}
                aria-current={
                  isPathActive(location.pathname, '/subscribe')
                    ? 'page'
                    : undefined
                }
              >
                <BellOutlined aria-hidden />
                <span>Job Alerts</span>
              </Link>
              <Link
                to="/improve-your-resume"
                className={navLinkClass(
                  isPathActive(location.pathname, '/improve-your-resume')
                )}
                aria-current={
                  isPathActive(location.pathname, '/improve-your-resume')
                    ? 'page'
                    : undefined
                }
              >
                <FileTextOutlined aria-hidden />
                <span>Resume</span>
              </Link>
              <Link
                to="/ats-check"
                className={navLinkClass(
                  isPathActive(location.pathname, '/ats-check')
                )}
                aria-current={
                  isPathActive(location.pathname, '/ats-check')
                    ? 'page'
                    : undefined
                }
              >
                <AuditOutlined aria-hidden />
                <span>ATS Check</span>
              </Link>
              <Link
                to="/resume-authenticity"
                className={navLinkClass(
                  isPathActive(location.pathname, '/resume-authenticity')
                )}
                aria-current={
                  isPathActive(location.pathname, '/resume-authenticity')
                    ? 'page'
                    : undefined
                }
              >
                <SafetyCertificateOutlined aria-hidden />
                <span>Authenticity</span>
              </Link>
              <Link
                to="/sign-up"
                className={navLinkClass(
                  isPathActive(location.pathname, '/sign-up')
                )}
                aria-current={
                  isPathActive(location.pathname, '/sign-up')
                    ? 'page'
                    : undefined
                }
              >
                <UserAddOutlined aria-hidden />
                <span>Post Jobs</span>
              </Link>
            </nav>
            <ThemeToggle className="public-header-theme hidden lg:inline-flex" />
            <div className="lg:hidden">
              <MobileMenu />
            </div>
          </div>
        </div>
      </header>

      <div className="flex w-full flex-grow flex-col">
        <Outlet />
      </div>

      <footer className="public-site-footer mt-auto w-full pb-4 pt-8 text-start">
        <div className="public-content-rail">
          <div className="public-site-footer__main">
            <div className="public-site-footer__brand">
              <SiteBrand
                to="/"
                layout="stacked"
                markSize={48}
                className="mb-3"
                wordmarkClassName="text-sm font-black uppercase leading-tight tracking-[0.08em] sm:text-base"
              />
              <p className="jo-body-muted mb-0 text-sm leading-6">
                Tech job board, daily alerts, and career tools for candidates
                and recruiters.
              </p>
            </div>

            <div className="public-site-footer__column">
              <h4 className="jo-heading mb-3 text-sm font-black">Explore</h4>
              <div className="flex flex-col space-y-2">
                <Link to="/" className="jo-footer-link">
                  <HomeOutlined />
                  Home
                </Link>
                <Link to="/jobs" className="jo-footer-link">
                  <SearchOutlined />
                  Find Jobs
                </Link>
                <Link to="/subscribe" className="jo-footer-link">
                  <BellOutlined />
                  Job Alerts
                </Link>
                <Link to="/improve-your-resume" className="jo-footer-link">
                  <FileTextOutlined />
                  Improve Resume
                </Link>
                <Link to="/ats-check" className="jo-footer-link">
                  <AuditOutlined />
                  ATS Check
                </Link>
                <Link to="/resume-authenticity" className="jo-footer-link">
                  <SafetyCertificateOutlined />
                  Resume Authenticity
                </Link>
                {releaseWhatsNew.available &&
                  releaseWhatsNew.slides.length > 0 && (
                    <button
                      type="button"
                      className="jo-footer-link text-left"
                      onClick={releaseWhatsNew.open}
                    >
                      <RocketOutlined />
                      What&apos;s new
                    </button>
                  )}
              </div>
            </div>

            <div className="public-site-footer__column">
              <h4 className="jo-heading mb-3 text-sm font-black">
                For Recruiters
              </h4>
              <div className="flex flex-col space-y-2">
                <Link to="/sign-up" className="jo-footer-link">
                  <UserAddOutlined />
                  Post Jobs
                </Link>
              </div>
            </div>

            {mcpEnabled && (
              <div className="public-site-footer__column">
                <h4 className="jo-heading mb-3 text-sm font-black">
                  Developers
                </h4>
                <div className="flex flex-col space-y-2">
                  <Link to="/developers" className="jo-footer-link">
                    <ApiOutlined />
                    MCP for AI assistants
                  </Link>
                </div>
              </div>
            )}

            <div className="public-site-footer__column">
              <h4 className="jo-heading mb-3 text-sm font-black">Community</h4>
              <div className="flex flex-col space-y-2">
                {whatsappUrl && (
                  <a
                    href={whatsappUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="jo-footer-link jo-footer-link--neutral"
                  >
                    <FontAwesomeIcon
                      icon={faWhatsapp}
                      className="community-brand-icon--whatsapp"
                    />
                    WhatsApp
                  </a>
                )}
                {telegramUrl && (
                  <a
                    href={telegramUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="jo-footer-link jo-footer-link--neutral"
                  >
                    <FontAwesomeIcon
                      icon={faTelegramPlane}
                      className="community-brand-icon--telegram"
                    />
                    Telegram
                  </a>
                )}
                {contactEmail && (
                  <a href={`mailto:${contactEmail}`} className="jo-footer-link">
                    <MailOutlined />
                    Contact
                  </a>
                )}
              </div>
            </div>
          </div>

          <div className="jo-divider public-site-footer__bottom mt-6 border-t pt-4">
            <p className="jo-body-muted mb-0 text-sm">
              &copy; {new Date().getFullYear()} Jobs & Opportunities. All rights
              reserved.
            </p>
            <nav className="public-site-footer__legal" aria-label="Legal">
              <Link to="/privacy" className="jo-footer-link">
                Private Policy
              </Link>
              <Link to="/terms" className="jo-footer-link">
                Terms and Conditions
              </Link>
            </nav>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Public;
